import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import toast from 'components/Basic/Toast';
import { useAppDispatch, useAppSelector } from 'utilities/redux';
import {
  useLazyGetRaffleImagesConfigQuery,
  useSaveRaffleImagesConfigMutation,
} from 'core/worldCup/raffleImages/RaffleImagesService';
import { setRaffleImages } from 'core/worldCup/raffleImages/RaffleImagesSlice';
import { CampaignImageType } from 'core/worldCup/raffleImages/types';
import { getApiErrorMessage } from 'utilities/getApiErrorMessages';

const useRaffleImages = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [files, setFiles] = useState<Partial<Record<CampaignImageType, File>>>({});

  const dispatch = useAppDispatch();
  const intl = useIntl();
  const [getConfig] = useLazyGetRaffleImagesConfigQuery();
  const [saveConfig] = useSaveRaffleImagesConfigMutation();

  const { images } = useAppSelector(state => state.raffleImagesReducer);
  const safeImages = Array.isArray(images) ? images : [];

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setIsLoading(true);
    try {
      const res = await getConfig().unwrap();
      dispatch(setRaffleImages(res));
    } catch (error) {
      console.error('Error loading raffle images config:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getImageByType = (type: CampaignImageType) => safeImages.find(img => img.type === type);

  const handleFileChange = (type: CampaignImageType, file: File | null) => {
    setFiles(prev => ({ ...prev, [type]: file ?? undefined }));
  };

  const hasFileChanged = Object.values(files).some(Boolean);

  const isSaveEnabled =
    (!!files[CampaignImageType.Banner] || !!getImageByType(CampaignImageType.Banner)?.url) &&
    (!!files[CampaignImageType.Main] || !!getImageByType(CampaignImageType.Main)?.url) &&
    hasFileChanged;

  const handleSave = async () => {
    if (!isSaveEnabled) return;

    setIsSaving(true);
    const formData = new FormData();
    if (files[CampaignImageType.Banner]) formData.append('bannerImage', files[CampaignImageType.Banner]!);
    if (files[CampaignImageType.Main]) formData.append('mainImage', files[CampaignImageType.Main]!);

    try {
      const res = await saveConfig(formData).unwrap();
      dispatch(setRaffleImages(res));
      setFiles({});
      toast.success({ title: intl.formatMessage({ id: 'Raffle images saved successfully!' }) });
    } catch (e: any) {
      console.error('Error saving raffle images config:', e);
      const defaultMsg = intl.formatMessage({ id: 'Saving raffle images failed!' });
      toast.error({ title: getApiErrorMessage(e, intl.locale, defaultMsg) });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    isLoading,
    isSaving,
    isSaveEnabled,
    getImageByType,
    handleFileChange,
    handleSave,
  };
};

export default useRaffleImages;
